import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { submitBooking } from '../services/api';

const BookingForm = () => {
  const [formData, setFormData] = useState({
    brideName: '',
    groomName: '',
    contactNumber: '',
    brideLocation: '',
    groomLocation: '',
    outfitCount: '',
    robeDescription: '',
    outfitOther: '',
    weddingType: '',
    weddingTypeOther: '',
    vendors: {
      wedding: '',
      makeup: '',
      butler: '',
      photography: ''
    },
    mustShoot: '',
    parentsStatus: '',
    groomsmen: '',
    bridesmaids: '',
    bridePersonality: '',
    bridePersonalityOther: '',
    brideAngle: '',
    groomPersonality: '',
    groomPersonalityOther: '',
    pregnancyStatus: '',
    props: '',
    avoidShoot: '',
    avoidShootDetail: '',
    videoSuggestions: '',
    quickEdit: '',
    videoStyle: '',
    colorTone: ''
  });

  // 添加loading、error和success状态
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 重置状态
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);

    try {
      // 调用API提交预约
      await submitBooking(formData);

      // 设置成功状态
      setSubmitSuccess(true);

      // 3秒后重置表单
      setTimeout(() => {
        setFormData({
          brideName: '',
          groomName: '',
          contactNumber: '',
          brideLocation: '',
          groomLocation: '',
          outfitCount: '',
          robeDescription: '',
          outfitOther: '',
          weddingType: '',
          weddingTypeOther: '',
          vendors: {
            wedding: '',
            makeup: '',
            butler: '',
            photography: ''
          },
          mustShoot: '',
          parentsStatus: '',
          groomsmen: '',
          bridesmaids: '',
          bridePersonality: '',
          bridePersonalityOther: '',
          brideAngle: '',
          groomPersonality: '',
          groomPersonalityOther: '',
          pregnancyStatus: '',
          props: '',
          avoidShoot: '',
          avoidShootDetail: '',
          videoSuggestions: '',
          quickEdit: '',
          videoStyle: '',
          colorTone: ''
        });
        setSubmitSuccess(false);
        // 滚动到顶部
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 3000);

    } catch (error) {
      console.error('提交失败:', error);
      setSubmitError(error.message || '提交失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">先问影像</h1>
              <p className="text-xs text-gray-500">影像项目需求咨询</p>
            </div>
            <img
              src="/images/askfirst-logo.png"
              alt="先问 ASKFIRST"
              className="h-12 w-20 object-contain rounded invert"
            />
          </div>
        </div>
      </div>

      {/* Form Container */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">预约拍摄服务</h2>
            <p className="text-sm text-gray-600">请填写以下信息，我们将为您定制专属拍摄方案</p>
          </div>

          {/* 成功消息 */}
          {submitSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-green-900">提交成功！</h3>
                <p className="text-sm text-green-700 mt-1">
                  预约信息已成功提交，我们会尽快与您联系确认拍摄详情。感谢您的信任！
                </p>
              </div>
            </div>
          )}

          {/* 错误消息 */}
          {submitError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-900">提交失败</h3>
                <p className="text-sm text-red-700 mt-1">{submitError}</p>
                <button
                  type="button"
                  onClick={() => setSubmitError(null)}
                  className="text-xs text-red-600 underline mt-2 hover:text-red-800"
                >
                  关闭
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 1. 新人姓名 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">1. 新人姓名 <span className="text-red-500">*</span></h3>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  name="brideName"
                  placeholder="客户 A 姓名"
                  value={formData.brideName}
                  onChange={handleInputChange}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                />
                <input
                  type="text"
                  name="groomName"
                  placeholder="客户 B 姓名"
                  value={formData.groomName}
                  onChange={handleInputChange}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                />
              </div>
            </div>

            {/* 2. 联系方式 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">2. 联系方式 <span className="text-red-500">*</span></h3>
              <input
                type="tel"
                name="contactNumber"
                placeholder="请输入手机号码"
                value={formData.contactNumber}
                onChange={handleInputChange}
                required
                pattern="[0-9]{11}"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
              />
            </div>

            {/* 3. 客户 A 拍摄地点 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">3. 客户 A 拍摄地点 <span className="text-red-500">*</span></h3>
              <textarea
                name="brideLocation"
                placeholder="客户 A 的拍摄地点，需要精确到门牌号"
                value={formData.brideLocation}
                onChange={handleInputChange}
                required
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
              />
            </div>

            {/* 4. 客户 B 拍摄地点 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">4. 客户 B 拍摄地点 <span className="text-red-500">*</span></h3>
              <textarea
                name="groomLocation"
                placeholder="客户 B 的拍摄地点，需要精确到门牌号"
                value={formData.groomLocation}
                onChange={handleInputChange}
                required
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
              />
            </div>

            {/* 5. 项目服装/造型数量 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">5. 项目当天预计使用几套服装或造型</h3>
              <div className="space-y-2">
                {[
                  { value: '1', label: '1套（主纱）' },
                  { value: '2', label: '2套（主纱、秀禾）' },
                  { value: '3', label: '3套（主纱、秀禾或出门纱、晨袍）' },
                  { value: '4', label: '4套（主纱、秀禾或出门纱、晨袍、迎宾服）' },
                  { value: '5+', label: '5套及以上' },
                  { value: 'other', label: '其他' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                    <input
                      type="radio"
                      name="outfitCount"
                      value={option.value}
                      checked={formData.outfitCount === option.value}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
                {formData.outfitCount === '3' && (
                  <textarea
                    name="robeDescription"
                    placeholder="请描述晨袍颜色和类型"
                    value={formData.robeDescription}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full mt-2 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
                  />
                )}
                {formData.outfitCount === 'other' && (
                  <input
                    type="text"
                    name="outfitOther"
                    placeholder="请说明服装情况"
                    value={formData.outfitOther}
                    onChange={handleInputChange}
                    className="w-full mt-2 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                  />
                )}
              </div>
            </div>

            {/* 6. 项目类型 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">6. 项目类型</h3>
              <div className="space-y-2">
                {['室内', '室外', '其他'].map(type => (
                  <label key={type} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                    <input
                      type="radio"
                      name="weddingType"
                      value={type}
                      checked={formData.weddingType === type}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{type}</span>
                  </label>
                ))}
                {formData.weddingType === '其他' && (
                  <input
                    type="text"
                    name="weddingTypeOther"
                    placeholder="请说明项目类型或主题"
                    value={formData.weddingTypeOther}
                    onChange={handleInputChange}
                    className="w-full mt-2 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                  />
                )}
              </div>
            </div>

            {/* 7. 合作服务 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">7. 已确定的合作服务（如有）</h3>
              <div className="space-y-2">
                <input
                  type="text"
                  name="vendors.wedding"
                  placeholder="合作服务"
                  value={formData.vendors.wedding}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                />
                <input
                  type="text"
                  name="vendors.makeup"
                  placeholder="化妆"
                  value={formData.vendors.makeup}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                />
                <input
                  type="text"
                  name="vendors.butler"
                  placeholder="管家"
                  value={formData.vendors.butler}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                />
                <input
                  type="text"
                  name="vendors.photography"
                  placeholder="摄影"
                  value={formData.vendors.photography}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                />
              </div>
            </div>

            {/* 8. 必拍的环节和来宾 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">8. 必拍的环节和来宾</h3>
              <textarea
                name="mustShoot"
                placeholder="请描述必须拍摄的环节和重要来宾"
                value={formData.mustShoot}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
              />
            </div>

            {/* 9. 双方父母情况 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">9. 双方父母是否有单亲或离异情况需要特别注意</h3>
              <textarea
                name="parentsStatus"
                placeholder="如有特殊情况请说明，没有请填写'无'"
                value={formData.parentsStatus}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
              />
            </div>

            {/* 10. 伴郎伴娘人数 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">10. 伴郎伴娘人数</h3>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  name="groomsmen"
                  placeholder="伴郎人数"
                  value={formData.groomsmen}
                  onChange={handleInputChange}
                  min="0"
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                />
                <input
                  type="number"
                  name="bridesmaids"
                  placeholder="伴娘人数"
                  value={formData.bridesmaids}
                  onChange={handleInputChange}
                  min="0"
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                />
              </div>
            </div>

            {/* 11. 客户 A 出镜偏好 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">11. 客户 A 的出镜偏好</h3>
              <div className="space-y-2">
                {[
                  { value: 'auto', label: '自然出镜（面对镜头时能自主呈现喜欢的状态）' },
                  { value: 'outgoing', label: '外向有活力（摆姿势可以放得开，可以充分调动自身情绪）' },
                  { value: 'slightly_outgoing', label: '较外向（需要跟拍老师简单引导情绪）' },
                  { value: 'slightly_introverted', label: '较内向（需要大幅度引导情绪）' },
                  { value: 'introverted', label: '内向（比较社恐）' },
                  { value: 'other', label: '其他' }
                ].map(option => (
                  <label key={option.value} className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                    <input
                      type="radio"
                      name="bridePersonality"
                      value={option.value}
                      checked={formData.bridePersonality === option.value}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 mt-0.5"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
                {formData.bridePersonality === 'other' && (
                  <input
                    type="text"
                    name="bridePersonalityOther"
                    placeholder="请说明"
                    value={formData.bridePersonalityOther}
                    onChange={handleInputChange}
                    className="w-full mt-2 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                  />
                )}
              </div>
            </div>

            {/* 12. 客户 A 喜欢的拍摄角度 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">12. 客户 A 喜欢的拍摄角度</h3>
              <div className="space-y-2">
                {['都可以', '左脸', '右脸', '正脸'].map(angle => (
                  <label key={angle} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                    <input
                      type="radio"
                      name="brideAngle"
                      value={angle}
                      checked={formData.brideAngle === angle}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{angle}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 13. 客户 B 出镜偏好 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">13. 客户 B 的出镜偏好</h3>
              <div className="space-y-2">
                {[
                  { value: 'auto', label: '自然出镜（面对镜头时能自主呈现喜欢的状态）' },
                  { value: 'outgoing', label: '外向有活力（摆姿势可以放得开，可以充分调动自身情绪）' },
                  { value: 'slightly_outgoing', label: '较外向（需要影像老师简单引导情绪）' },
                  { value: 'slightly_introverted', label: '较内向（需要大幅度引导情绪）' },
                  { value: 'introverted', label: '内向（比较社恐）' },
                  { value: 'other', label: '其他' }
                ].map(option => (
                  <label key={option.value} className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                    <input
                      type="radio"
                      name="groomPersonality"
                      value={option.value}
                      checked={formData.groomPersonality === option.value}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 mt-0.5"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
                {formData.groomPersonality === 'other' && (
                  <input
                    type="text"
                    name="groomPersonalityOther"
                    placeholder="请说明"
                    value={formData.groomPersonalityOther}
                    onChange={handleInputChange}
                    className="w-full mt-2 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                  />
                )}
              </div>
            </div>

            {/* 14. 客户 A 是否有身体状况或拍摄注意事项 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">14. 是否有身体状况或拍摄注意事项</h3>
              <textarea
                name="pregnancyStatus"
                placeholder="如有请说明，没有请填写'无'"
                value={formData.pregnancyStatus}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
              />
            </div>

            {/* 15. 拍摄道具 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">15. 项目准备了哪些拍摄道具</h3>
              <textarea
                name="props"
                placeholder="如果有请详细说明"
                value={formData.props}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
              />
            </div>

            {/* 16. 不想被记录的环节 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">16. 是否有特别不想被记录的环节，或者习俗上的禁忌</h3>
              <div className="space-y-2">
                {['无', '有'].map(option => (
                  <label key={option} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                    <input
                      type="radio"
                      name="avoidShoot"
                      value={option}
                      checked={formData.avoidShoot === option}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{option}</span>
                  </label>
                ))}
                {formData.avoidShoot === '有' && (
                  <textarea
                    name="avoidShootDetail"
                    placeholder="请详细说明"
                    value={formData.avoidShootDetail}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full mt-2 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
                  />
                )}
              </div>
            </div>

            {/* 关于视频部分 */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h2 className="text-lg font-bold mb-4">关于视频</h2>

              {/* 1. 后期视频剪辑建议 */}
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-medium text-gray-700">1. 后期视频剪辑建议</h3>
                <textarea
                  name="videoSuggestions"
                  placeholder="请输入您对视频剪辑的建议"
                  value={formData.videoSuggestions}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
                />
              </div>

              {/* 2. 是否预订快剪 */}
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-medium text-gray-700">2. 是否预订快剪</h3>
                <div className="flex gap-4">
                  {['是', '否'].map(option => (
                    <label key={option} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                      <input
                        type="radio"
                        name="quickEdit"
                        value={option}
                        checked={formData.quickEdit === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 3. 对视频的剪辑风格是否有要求 */}
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-medium text-gray-700">3. 对视频的剪辑风格是否有要求</h3>
                <textarea
                  name="videoStyle"
                  placeholder="请描述您喜欢的剪辑风格"
                  value={formData.videoStyle}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
                />
              </div>

              {/* 4. 对视频色调是否有要求 */}
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-medium text-gray-700">4. 对视频色调是否有要求</h3>
                <textarea
                  name="colorTone"
                  placeholder="请描述您喜欢的色调风格"
                  value={formData.colorTone}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none"
                />
              </div>
            </div>

            {/* 提交按钮 */}
            <div className="pt-8">
              <button
                type="submit"
                disabled={isSubmitting || submitSuccess}
                className={`w-full py-4 rounded-lg font-medium transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 ${
                  isSubmitting || submitSuccess
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    提交中...
                  </>
                ) : submitSuccess ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    提交成功
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    提交预约信息
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center py-8 text-xs text-gray-500">
          <p>© 2024 先问影像 | 影像项目服务</p>
          <p className="mt-1">客服电话：400-888-8888</p>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
